<!--
  Singleton ConfirmDialog - mounted once in App.vue
  Reacts to dialogStore state for showing/hiding
-->

<template>
  <base-modal
    v-model="isVisible"
    :title="currentTitle"
    :primary-color="currentPrimaryColor"
    :width="currentWidth"
    :show-row2="false"
    :show-footer="true"
    :auto-focus-primary-action="true"
    :hide-secondary-action="currentHideCancelButton"
    :primary-action-text="withShortcut(currentOkLabel, shortcuts.SAVE)"
    :secondary-action-text="withShortcut(currentCancelLabel, shortcuts.CANCEL)"
    @save="handleOk"
    @cancel="handleCancel"
    @hidden="onHidden"
  >
    <div class="tw-p-4">
      <div class="tw-flex tw-items-start tw-gap-2">
        <!-- Optional icon -->
        <octicon
          v-if="currentIcon"
          :icon="currentIcon"
          :width="16"
          :height="16"
          class="tw-mt-0.5 tw-shrink-0"
          :class="iconColorClass"
        />
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="tw-flex-1 tw-text-sm tw-text-gray-dark"
          v-html="currentMessage"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Colors } from '@/api';
import BaseModal from '@/components/modal/templates/BaseModal.vue';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { useDialogStore } from '@/store/dialogStore';
import { storeToRefs } from 'pinia';
import DOMPurify from 'dompurify';

const { shortcuts, withShortcut } = useKeyboardShortcuts();

const dialogStore = useDialogStore();
const {
  isVisible: storeIsVisible,
  options,
  triggerElement,
} = storeToRefs(dialogStore);

// Local visibility state synced with store
const isVisible = ref(false);

// Computed properties derived from store options
// Message is sanitized with DOMPurify to prevent XSS attacks
const currentMessage = computed(() =>
  DOMPurify.sanitize(options.value?.message || '')
);
const currentTitle = computed(() => options.value?.title || 'Confirm');
const currentPrimaryColor = computed(() => options.value?.color || Colors.BLUE);
const currentOkLabel = computed(() => options.value?.okLabel || 'OK');
const currentCancelLabel = computed(
  () => options.value?.cancelLabel || 'Cancel'
);
const currentHideCancelButton = computed(
  () => options.value?.hideCancelButton || false
);
const currentIcon = computed(() => options.value?.icon || '');
const currentWidth = computed(() => options.value?.width || '450px');

// Icon color based on dialog color
const iconColorClass = computed(() => {
  const colorMap: Record<Colors, string> = {
    [Colors.GRAY_MEDIUM_DARK]: 'tw-text-gray-medium-dark',
    [Colors.RED]: 'tw-text-danger',
    [Colors.PINK]: 'tw-text-pink',
    [Colors.ORANGE]: 'tw-text-orange',
    [Colors.YELLOW]: 'tw-text-warning',
    [Colors.LIME]: 'tw-text-lime',
    [Colors.GREEN]: 'tw-text-green',
    [Colors.TEAL]: 'tw-text-teal',
    [Colors.BLUE]: 'tw-text-blue',
    [Colors.BLUE_MEDIUM]: 'tw-text-blue',
    [Colors.PURPLE]: 'tw-text-purple',
  };
  return colorMap[currentPrimaryColor.value] || 'tw-text-blue';
});

// Watch store visibility and sync to local state
watch(
  storeIsVisible,
  (newValue) => {
    isVisible.value = newValue;
  },
  { immediate: true }
);

// Watch local visibility changes and sync back to store
watch(isVisible, (newValue) => {
  if (!newValue && storeIsVisible.value) {
    dialogStore.hide();
  }
});

async function handleOk() {
  if (options.value?.onOk) {
    await options.value.onOk();
  }
  dialogStore.hide();
}

async function handleCancel() {
  if (options.value?.onCancel) {
    await options.value.onCancel();
  }
  dialogStore.hide();
}

function onHidden() {
  // Return focus to trigger if it still exists in DOM
  if (
    triggerElement.value &&
    typeof triggerElement.value.focus === 'function' &&
    document.contains(triggerElement.value)
  ) {
    // Ensure trigger element is focusable (important for table cells, divs, etc.)
    if (!triggerElement.value.hasAttribute('tabindex')) {
      triggerElement.value.setAttribute('tabindex', '-1');
    }

    // Use requestAnimationFrame to ensure focus happens after modal is fully hidden
    requestAnimationFrame(() => {
      // Re-check that element is still in DOM after rAF (defensive check)
      if (triggerElement.value && document.contains(triggerElement.value)) {
        triggerElement.value.focus();
      }
    });
  }

  // Reset store state after dialog is fully hidden
  dialogStore.reset();
}
</script>
